import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box, Typography, Button, Paper, Alert,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { axios_tenant } from "../../../../axios";
import OAWorkflowUploadUI from "./components/OAWorkflowUploadUI";
import OAPagination from "../../../shared/OAWorkflow/components/OAPagination";
import { useDocumentTitle } from "../../../shared/OAWorkflow/utils/useDocumentTitle";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import "./OAWorkflow.css";

/**
 * Excel upload page with drag-and-drop and import result summary.
 * Matches Figma design using the shared OAWorkflowUploadUI component.
 * Route: /collaboration-space/oa-workflow/intake
 */
const OAWorkflowIntakeUpload = () => {
    useDocumentTitle("OA Shell — Upload");
    const navigate = useNavigate();
    const outerTheme = useTheme();

    const localTheme = React.useMemo(() => createTheme(outerTheme, {
        typography: {
            fontFamily: 'Lato, sans-serif',
        },
    }), [outerTheme]);

    const [result, setResult] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const handleUploadSuccess = (data) => {
        setResult(data);
        setPage(0); // Reset page on new result
    };

    return (
        <ThemeProvider theme={localTheme}>
            <Box className="oa-lato-font" sx={{ pt: 1, px: 4, pb: 10, position: 'relative' }} data-testid="oa-workflow-intake-page">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
                    <Button
                        size="small"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate("/collaboration-space/oa-workflow")}
                        sx={{ textTransform: "none", color: "#666" }}
                    >
                        Back to Tasks
                    </Button>
                </Box>

                <Paper elevation={0} sx={{ borderRadius: 2, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
                    {/* OAWorkflowUploadUI handles the title internally as "Upload File" per Figma */}
                    <OAWorkflowUploadUI onUploadSuccess={handleUploadSuccess} />

                    {/* Results Section */}
                    {result && (
                        <Box sx={{ p: 4, pt: 0 }}>
                            <Paper sx={{ p: 3, borderRadius: 2, backgroundColor: "#F9FBF9", border: "1px solid #E8F5E9" }} elevation={0} data-testid="oa-intake-result">
                                <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: "#2E7D32" }}>Import Results</Typography>
                                <Box sx={{ display: "flex", gap: 3, mb: 2 }}>
                                    <Typography><strong>Total:</strong> {result.totalRecords}</Typography>
                                    <Typography color="success.main"><strong>Imported:</strong> {result.imported}</Typography>
                                    <Typography color="error.main"><strong>Rejected:</strong> {result.rejected}</Typography>
                                </Box>

                                {result.rejectedRows?.length > 0 && (
                                    <Box>
                                        <TableContainer sx={{ maxHeight: 300, mt: 2, minHeight: 150 }}>
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 700, backgroundColor: "#F9FBF9", width: 80 }}>Row</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, backgroundColor: "#F9FBF9" }}>Errors</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {result.rejectedRows.slice(page * rowsPerPage, (page + 1) * rowsPerPage).map((r, i) => (
                                                        <TableRow key={i}>
                                                            <TableCell>{r.row}</TableCell>
                                                            <TableCell sx={{ color: "#D32F2F" }}>{r.errors.join("; ")}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        <OAPagination
                                            count={result.rejectedRows.length}
                                            page={page}
                                            rowsPerPage={rowsPerPage}
                                            onPageChange={setPage}
                                            onRowsPerPageChange={(val) => { setRowsPerPage(val); setPage(0); }}
                                        />
                                    </Box>
                                )}
                            </Paper>
                        </Box>
                    )}
                </Paper>
            </Box>
        </ThemeProvider>
    );
};

export default OAWorkflowIntakeUpload;
